import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import {
  Webhook,
  Plus,
  Copy,
  Trash2,
  Edit,
  Loader2,
  AlertTriangle,
  MoreHorizontal,
  Check,
  Shield,
  Play,
  X,
} from "lucide-react";

// ==================== TYPES ====================

interface PartnerWebhook {
  id: string;
  url: string;
  events: string[];
  status: "active" | "disabled";
  description: string | null;
  failure_count: number;
  last_delivered_at: string | null;
  disabled_at: string | null;
  created_at: string;
  updated_at: string;
  secret?: string;
}

// ==================== CONSTANTS ====================

const WEBHOOK_EVENTS = [
  { value: "card.created", label: "Card Created", description: "A new virtual card was issued" },
  { value: "card.updated", label: "Card Updated", description: "Card details were modified" },
  { value: "card.frozen", label: "Card Frozen", description: "Card was frozen" },
  { value: "card.unfrozen", label: "Card Unfrozen", description: "Card was unfrozen" },
  { value: "card.cancelled", label: "Card Cancelled", description: "Card was permanently cancelled" },
];

// ==================== HELPERS ====================

function getStatusBadge(status: string) {
  switch (status) {
    case "active":
      return <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-0">Active</Badge>;
    case "disabled":
      return <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0">Disabled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function formatDateTime(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatEvents(events: string[]) {
  if (events.includes("*")) return <Badge variant="outline" className="text-xs">All Events</Badge>;
  if (events.length <= 2) {
    return events.map((e) => (
      <Badge key={e} variant="secondary" className="text-xs mr-1">{e}</Badge>
    ));
  }
  return (
    <>
      <Badge variant="secondary" className="text-xs mr-1">{events[0]}</Badge>
      <Badge variant="outline" className="text-xs">+{events.length - 1} more</Badge>
    </>
  );
}

// ==================== COMPONENT ====================

export default function WebhooksPage() {
  const { toast } = useToast();

  // UI state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<PartnerWebhook | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PartnerWebhook | null>(null);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [newWebhookUrl, setNewWebhookUrl] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);

  // Create form state
  const [createForm, setCreateForm] = useState({
    url: "",
    events: [] as string[],
    description: "",
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    url: "",
    events: [] as string[],
    description: "",
    status: "active" as "active" | "disabled",
  });

  // ==================== QUERIES ====================

  const { data: webhooks, isLoading } = useQuery<PartnerWebhook[]>({
    queryKey: ["partner-webhooks"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/partner-webhooks");
      return response.json();
    },
  });

  // ==================== MUTATIONS ====================

  const createMutation = useMutation({
    mutationFn: async (data: typeof createForm) => {
      const response = await apiRequest("POST", "/api/admin/partner-webhooks", data);
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["partner-webhooks"] });
      setNewSecret(data.secret);
      setNewWebhookUrl(data.url);
      setIsCreateOpen(false);
      resetCreateForm();
    },
    onError: (error) => {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await apiRequest("PATCH", `/api/admin/partner-webhooks/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-webhooks"] });
      toast({ title: "Success", description: "Webhook updated" });
      setIsEditOpen(false);
      setEditingWebhook(null);
    },
    onError: (error) => {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/admin/partner-webhooks/${id}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-webhooks"] });
      toast({ title: "Deleted", description: "Webhook has been deleted" });
      setDeleteTarget(null);
    },
    onError: (error) => {
      toast({ title: "Error", description: String(error), variant: "destructive" });
    },
  });

  // ==================== FORM HELPERS ====================

  function resetCreateForm() {
    setCreateForm({ url: "", events: [], description: "" });
  }

  function toggleEvent(event: string, formType: "create" | "edit") {
    if (formType === "create") {
      setCreateForm((prev) => ({
        ...prev,
        events: prev.events.includes(event)
          ? prev.events.filter((e) => e !== event)
          : [...prev.events, event],
      }));
    } else {
      setEditForm((prev) => ({
        ...prev,
        events: prev.events.includes(event)
          ? prev.events.filter((e) => e !== event)
          : [...prev.events, event],
      }));
    }
  }

  function openEditDialog(webhook: PartnerWebhook) {
    setEditingWebhook(webhook);
    setEditForm({
      url: webhook.url,
      events: [...webhook.events],
      description: webhook.description || "",
      status: webhook.status,
    });
    setIsEditOpen(true);
  }

  function copySecret() {
    if (newSecret) {
      navigator.clipboard.writeText(newSecret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
      toast({ title: "Copied", description: "Webhook secret copied to clipboard" });
    }
  }

  // ==================== RENDER ====================

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Webhook className="h-6 w-6" /> Webhooks
          </h1>
          <p className="text-muted-foreground mt-1">
            Receive real-time notifications for card events
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" /> Add Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Webhook Endpoint</DialogTitle>
              <DialogDescription>
                Register a URL to receive webhook events. The signing secret will only be shown once.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!createForm.url || createForm.events.length === 0) {
                  toast({ title: "Validation Error", description: "URL and at least one event are required", variant: "destructive" });
                  return;
                }
                createMutation.mutate(createForm);
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label htmlFor="webhook-url">Endpoint URL</Label>
                <Input
                  id="webhook-url"
                  type="url"
                  placeholder="https://example.com/webhooks"
                  value={createForm.url}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, url: e.target.value }))}
                  required
                />
                <p className="text-xs text-muted-foreground">Must use HTTPS</p>
              </div>

              <div className="space-y-2">
                <Label>Events</Label>
                <div className="space-y-2 border rounded-lg p-3">
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Checkbox
                      id="event-all"
                      checked={createForm.events.includes("*")}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setCreateForm((prev) => ({ ...prev, events: ["*"] }));
                        } else {
                          setCreateForm((prev) => ({ ...prev, events: [] }));
                        }
                      }}
                    />
                    <label htmlFor="event-all" className="text-sm font-medium cursor-pointer">
                      All Events (*)
                    </label>
                  </div>
                  {WEBHOOK_EVENTS.map((event) => (
                    <div key={event.value} className="flex items-start gap-2">
                      <Checkbox
                        id={`event-${event.value}`}
                        checked={createForm.events.includes("*") || createForm.events.includes(event.value)}
                        disabled={createForm.events.includes("*")}
                        onCheckedChange={() => toggleEvent(event.value, "create")}
                      />
                      <label htmlFor={`event-${event.value}`} className="cursor-pointer">
                        <div className="text-sm font-medium">{event.label}</div>
                        <div className="text-xs text-muted-foreground">{event.description}</div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhook-desc">Description (optional)</Label>
                <Textarea
                  id="webhook-desc"
                  placeholder="e.g., Production event handler"
                  value={createForm.description}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Create Webhook
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Webhooks Table */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : !webhooks || webhooks.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <Webhook className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold">No Webhooks</h3>
            <p className="text-muted-foreground text-sm mt-1">Add a webhook endpoint to receive real-time event notifications</p>
            <Button className="mt-4" onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Add Webhook
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>URL</TableHead>
                <TableHead>Events</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Failures</TableHead>
                <TableHead>Last Delivery</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks.map((webhook) => (
                <TableRow key={webhook.id}>
                  <TableCell>
                    <div className="max-w-[250px]">
                      <div className="font-mono text-xs truncate">{webhook.url}</div>
                      {webhook.description && (
                        <div className="text-xs text-muted-foreground truncate mt-0.5">{webhook.description}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{formatEvents(webhook.events)}</TableCell>
                  <TableCell>{getStatusBadge(webhook.status)}</TableCell>
                  <TableCell>
                    {webhook.failure_count > 0 ? (
                      <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-0">
                        {webhook.failure_count}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(webhook.last_delivered_at)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(webhook.created_at)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(webhook)}>
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        {webhook.status === "disabled" && (
                          <DropdownMenuItem
                            onClick={() =>
                              updateMutation.mutate({
                                id: webhook.id,
                                data: { status: "active" },
                              })
                            }
                          >
                            <Play className="h-4 w-4 mr-2" /> Re-enable
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600"
                          onClick={() => setDeleteTarget(webhook)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Webhook</DialogTitle>
            <DialogDescription>Update webhook endpoint settings</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!editingWebhook) return;
              updateMutation.mutate({ id: editingWebhook.id, data: editForm });
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="edit-url">Endpoint URL</Label>
              <Input
                id="edit-url"
                type="url"
                value={editForm.url}
                onChange={(e) => setEditForm((prev) => ({ ...prev, url: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Events</Label>
              <div className="space-y-2 border rounded-lg p-3">
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Checkbox
                    id="edit-event-all"
                    checked={editForm.events.includes("*")}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setEditForm((prev) => ({ ...prev, events: ["*"] }));
                      } else {
                        setEditForm((prev) => ({ ...prev, events: [] }));
                      }
                    }}
                  />
                  <label htmlFor="edit-event-all" className="text-sm font-medium cursor-pointer">
                    All Events (*)
                  </label>
                </div>
                {WEBHOOK_EVENTS.map((event) => (
                  <div key={event.value} className="flex items-start gap-2">
                    <Checkbox
                      id={`edit-event-${event.value}`}
                      checked={editForm.events.includes("*") || editForm.events.includes(event.value)}
                      disabled={editForm.events.includes("*")}
                      onCheckedChange={() => toggleEvent(event.value, "edit")}
                    />
                    <label htmlFor={`edit-event-${event.value}`} className="cursor-pointer">
                      <div className="text-sm font-medium">{event.label}</div>
                      <div className="text-xs text-muted-foreground">{event.description}</div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-desc">Description</Label>
              <Textarea
                id="edit-desc"
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Webhook</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the webhook endpoint "{deleteTarget?.url}".
              Pending deliveries will not be retried. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* One-Time Secret Display Dialog */}
      <Dialog open={!!newSecret} onOpenChange={(open) => { if (!open) { setNewSecret(null); setSecretCopied(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> Webhook Created
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg border bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-medium">Store this signing secret securely — it will not be shown again.</p>
                  <p className="mt-1 text-amber-700 dark:text-amber-300">
                    You'll need this secret to verify webhook signatures.
                  </p>
                </div>
              </div>
            </div>
            {newWebhookUrl && (
              <div className="text-sm text-muted-foreground">Endpoint: <span className="font-mono text-foreground text-xs">{newWebhookUrl}</span></div>
            )}
            <div>
              <Label className="text-xs text-muted-foreground">Signing Secret</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 p-3 bg-muted rounded-lg text-sm font-mono break-all select-all">
                  {newSecret}
                </code>
                <Button variant="outline" size="icon" onClick={copySecret} className="shrink-0">
                  {secretCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => { setNewSecret(null); setSecretCopied(false); }}>
              I've saved the secret
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
